import * as api from '@/api';
import { getRouteParams, getSharingModes } from '@/utils/route-params';
import eventBus from '@/utils/event-bus';
import routeSettingsDefaults from "@data/routeSettingsDefaults";

export async function loadRouteResults({commit, getters, dispatch}, fitBounds = true) { 
    try {
        commit('setRoutesLoading', true);
        dispatch('clearRouteResults');
        let modes = getters.selectedTransportModeKeys;
        let promises = modes.map(mode => {
            return dispatch('loadRouteResultsByType', {
                type: mode,
                timeType: false,
                fitBounds
            });
        });
        await Promise.all(promises);
    } catch(error) {
        console.error(error)
    } finally {
        commit('upSearchId');
        commit('setRoutesLoading', false);
    }
}


export async function loadRouteResultsByType({commit, getters}, {type, timeType = false, fitBounds = true}) {
    return new Promise((resolve) => {
        commit('setTransportModeLoading', {type, loading: true});
        commit('setTransportModeError', {type, error: false});

        let promises = []
        if(type === 'SHARING') {
            const sharingModes = getSharingModes()
            promises = sharingModes.map(option => {
                const additionalParams = {
                    debug: true,
                    sharingOptions: {
                        variant: option
                    }
                }
                return getRouteResulstByTypePromise({commit, getters}, {type, timeType, fitBounds, additionalParams, multiRequest: true})
            })
        } else {
            promises = [getRouteResulstByTypePromise({commit, getters}, {type, timeType, fitBounds})]
        }

        Promise.allSettled(promises).then((response) => {
            let hasFullfilled
            let error = false
            // check if some promises are fullfilled
            response.forEach(res => {
                if(res.status === 'fulfilled') {
                    hasFullfilled = true
                } else {
                    error = res.reason
                }
            })
            // show error
            if(!hasFullfilled) {
                commit('setTransportModeError', {type, error: error.response ? error.response.status : 'undefined'});
                commit('setTransportModeLoading', {type, loading: false});
                resolve()
            }
            // find first response with fulfilled status (assuming data is the same for every request)
            const responseData = response.find(res => res.status === 'fulfilled')?.value?.data
            if(!responseData) return
            let routes = []
            response.forEach(res => {
                const resRoutes = res.value ? res.value.routes : []
                routes = [...routes, ...resRoutes]
            })

            // sort sharing routes by time
            if(type === 'SHARING') {
                routes = routes.sort((a, b) => { return a.duration - b.duration })
            }

            commit('setTransportModeLoading', {type, loading: false});
            commit('setRouteResultsByType', {type, routes});
            if(responseData.scrollInformation) {
                commit('setRouteResultsScrollInformation', {type, scrollInformation: responseData.scrollInformation});
            }
            if(responseData.shorttermParkingZones && responseData.shorttermParkingZones.length) {
                commit('setRouteShortTermParkingZone', {type, value: responseData.shorttermParkingZones[0]});
            }
            if(responseData.countryInfos && responseData.countryInfos.infos && responseData.countryInfos.infos.length) {
                commit('setRouteCountryInfo', {type, value: responseData.countryInfos.infos});
            }
            if(responseData.greenzones && responseData.greenzones.length) {
                commit('setRouteGreenzones', { type, value: responseData.greenzones })
            }
            if(routes.length > 0) eventBus.$emit('addIndexWaypoints')
            resolve()
        })
    })
}

function getRouteResulstByTypePromise({commit, getters}, {type, timeType = false, fitBounds = true, additionalParams = false}) {
    return new Promise((resolve, reject) => {
        let params = getRouteParams(type, timeType);

        if(getters.routeShortTermParkingZone[type]) {
            commit('setRouteShortTermParkingZone', {type, value: false});
        }
        if(!getters.selectedTransportModeKeys.includes(type)) {
            commit('setTransportModeSelected', type);
            commit('setManualTransportMode', type);
        }
        if(additionalParams) {
            params = {...params, ...additionalParams}
        }

        api.smr.route(params)
        .then(response => {
            const foundRoutes = response.data.journeys && response.data.journeys.length;
            let routes = foundRoutes ? response.data.journeys : [];
            routes = routes.map((route, index) => {
                route.id = `s-${getters.searchId}-route-${type}-${index}`;
                route.mode = type;
                route.fitBounds = fitBounds;
                if(type === 'SHARING') {
                    route.sharingVehicle = response.data.inputParameters.sharingOptions.variant
                    route.id += `-${route.sharingVehicle}`
                    route.duration = route.metadata.duration
                }
                return route; 
            })
            const data = {
                data: response.data,
                routes: routes
            }
            resolve(data)
        })
        .catch((error) => {
            console.error(error);
            reject(error);
        })
    })
}

export async function loadRouteResultsByTimeType({commit, getters}, timeType) {
    let type = getters.activeTransportModeTab;
    try {
        let params = getRouteParams(type, timeType);
        commit('setTransportModeLoading', {type, loading: true});
        commit('setTransportModeError', {type, error: false});
        const response = await api.smr.route(params) // live
        // const response = await api.smr.testRoute(params) // dev
        const foundRoutes = response.data.journeys && response.data.journeys.length;
        let routes = foundRoutes ? response.data.journeys : [];
        routes = routes.map((route, index) => {
            route.id = `s-${getters.searchId}-route-${type}-${index}`;
            route.mode = type;
            return route; 
        })
        commit('setRouteResultsByTimeType', {type, timeType, routes});
        if(response.data.scrollInformation) {
            // set scroll information only for earlier and later connections changes, otherwise order is messed up
            if(timeType === 'EARLIER') {
                commit('setRouteResultsScrollInformation', {type, scrollInformation: {earlierConnectionsHint: response.data.scrollInformation.earlierConnectionsHint}});
            } else if(timeType === 'LATER') {
                commit('setRouteResultsScrollInformation', {type, scrollInformation: {laterConnectionsHint: response.data.scrollInformation.laterConnectionsHint}});
            }
        }
    } catch(error) {
        console.error(error)
        commit('setTransportModeError', {type, error: error.response.status});
    } finally {
        commit('upSearchId');
        commit('setTransportModeLoading', {type, loading: false});
    }
}

export function clearRouteResults({commit}) {
    commit('removeAllRouteResults', {});
    commit('setActiveRouteId', '');
    commit('setActiveRouteMode', false);
    commit('setRouteCorridor', false);
    commit('setActiveRouteSaved', false)
    eventBus.$emit('removeRoutesFromMap')
}

export function setRouteSettings({dispatch, commit}, payload) {
    // if route settings are missing because it's a new transport mode, add the default ones
    Object.keys(routeSettingsDefaults).forEach(typeKey => {
        if(!payload[typeKey]) {
            payload[typeKey] = JSON.parse(JSON.stringify(routeSettingsDefaults[typeKey]))
        }
    })
    commit('setRouteSettings', payload);
    dispatch('setSelectedTransportModesFromSettings');
}

export function setSelectedTransportModesFromSettings({getters, commit}) {
    const selectedModes = [...getters.routeSettingsGeneralTypes];
    if(getters.manualTransportModes.length) {
        getters.manualTransportModes.forEach(modeKey => {
            if(!selectedModes.includes(modeKey)) {
                selectedModes.push(modeKey);
            }
        })
    }
    commit('setSelectedTransportModes', selectedModes)
}


export function setDetailRoute({commit}, {id}) {
    commit('setDetailRouteId', id)
}